import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Collapse, Skeleton } from 'antd'
import { DeleteOutlined, LinkOutlined } from '@ant-design/icons'
import './faqModal.scss'
import { useMutation } from 'react-query'
import { faqApiData, deleteUserGuide } from '../../services/faqApi'
import { useGlobalNotification } from '../../utils/notificationProvider'
import axios from 'axios'

const FaqModal = ({ visible, onClose }) => {
  const [activeTab, setActiveTab] = useState(1)
  const [faqData, setFaqData] = useState([])
  const [userGuideLoading, setUserGuideLoading] = useState(false)
  const [faqUserGuide, setFaqUserGuide] = useState([])
  const {
    isError: isFaqDataError,
    isLoading: isFaqDataLoading,
    data: fetchedFaqData,
    mutate: mutateFaqData
  } = useMutation(faqApiData)
  const { isLoading: isRemoveUserGuideLoading, mutate: mutateDeleteUserGuide } =
    useMutation(deleteUserGuide)
  const pushNotification = useGlobalNotification()
  useEffect(() => {
    if (visible && sessionStorage.getItem('user_id')) {
      mutateFaqData(
        {
          model: {
            filters: [],
            list_details: [],
            settings: {}
          },
          user_id: sessionStorage.getItem('user_id'),
          application_role: sessionStorage.getItem('application_role')
        },
        {
          onSuccess: () => {},
          onError: (error) => {
            console.error('Error fetching data', error)
          }
        }
      )
    }
  }, [visible, mutateFaqData])

  useEffect(() => {
    if (!isFaqDataError && !isFaqDataLoading && fetchedFaqData?.data?.result) {
      setFaqUserGuide(fetchedFaqData?.data?.result?.resultFaqUserguide)
      const data = fetchedFaqData?.data?.result?.tabs
      setFaqData(data)
      if (data && data.length > 0) {
        setActiveTab(data[0].id)
      }
    }
  }, [fetchedFaqData, isFaqDataError, isFaqDataLoading])

  useEffect(() => {
    if (visible && faqData.length > 0) {
      setActiveTab(faqData[0].id)
    }
  }, [visible, faqData])

  const handleTabClick = (tab) => {
    setActiveTab(tab)
  }

  const handleRemoveUserGuide = (userGuide) => {
    mutateDeleteUserGuide(
      {
        model: {
          filters: [],
          list_details: [],
          settings: {
            file_id: userGuide?.file_id,
            modified_by: sessionStorage.getItem('unique_user_name'),
            modified_by_name:
              sessionStorage.getItem('first_name')?.trim() &&
              sessionStorage.getItem('first_name').trim() !== 'null' &&
              sessionStorage.getItem('last_name')?.trim() &&
              sessionStorage.getItem('last_name').trim() !== 'null'
                ? `${sessionStorage.getItem('first_name').trim()} ${sessionStorage.getItem('last_name').trim()}`
                : sessionStorage.getItem('unique_user_name')
          }
        },
        user_id: sessionStorage.getItem('user_id'),
        application_role: sessionStorage.getItem('application_role')
      },
      {
        onSuccess: () => {
          setFaqUserGuide([])
        }
      }
    )
  }

  const renderContent = () => {
    const activeTabData = faqData.find((tab) => tab.id === activeTab)
    if (!activeTabData) return null
    return (
      <div>
        <h2>{activeTabData.title}</h2>
        <Collapse accordion style={{ border: '0px' }}>
          {activeTabData.questions.map((item, index) => (
            <Collapse.Panel header={item.question} key={`${index}-${item.question}`}>
              <ul>
                {item.answer
                  .split('-')
                  .filter(Boolean)
                  .map((point, idx) => (
                    <li key={idx}>{point.trim()}</li>
                  ))}
              </ul>
            </Collapse.Panel>
          ))}
        </Collapse>
      </div>
    )
  }

  const handleDownload = async (file) => {
    try {
      let fileData
      if (file instanceof Blob || file instanceof File) {
        fileData = file
      } else if (file.link) {
        const response = await fetch(file.link)
        if (!response.ok) throw new Error('Failed to fetch file')
        fileData = await response.blob()
      } else {
        throw new Error('Invalid file object')
      }

      const url = URL.createObjectURL(fileData)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name ?? file.file_name
      a.click()
      URL.revokeObjectURL(url)
      pushNotification(
        'success',
        'Data downloaded successfully!',
        `Attachment downloaded successfully.`,
        3
      )
    } catch (error) {
      // console.error("Download failed:", error);
      pushNotification('error', 'Download failed!', `There was an issue downloading the file.`, 3)
    }
  }

  const allowedTypes = useRef([
    'application/pdf',
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'text/plain' // .txt
  ])
  const handleFileUpload = async (e) => {
    const MAX_FILE_SIZE = 40
    const formData = new FormData()
    setUserGuideLoading(true)
    const file = e.target.files[0]
    if (!file) return
    if (!allowedTypes.current.includes(file.type)) {
      e.target.value = ''
      pushNotification(
        'error',
        'Error uploading user guide!',
        `Invalid file type. Please upload a PDF, Excel, Word, PPT, or TXT file.`,
        3
      )
      setUserGuideLoading(false)
      return
    }
    const fileSizeMB = file.size / (1024 * 1024)
    if (fileSizeMB > MAX_FILE_SIZE) {
      e.target.value = ''
      pushNotification(
        'error',
        'Error uploading user guide!',
        `File is too large. Maximum size is ${MAX_FILE_SIZE} MB.`,
        3
      )
      setUserGuideLoading(false)
      return
    }
    if (!file?.file_name) {
      formData.append('files', file) // Adjusted the key to 'files'
      formData.append('filename', file.name)
    }
    formData.append('user_id', sessionStorage.getItem('user_id'))
    formData.append('modified_by', sessionStorage.getItem('unique_user_name'))
    const modified_by_name =
      sessionStorage.getItem('first_name')?.trim() &&
      sessionStorage.getItem('first_name').trim() !== 'null' &&
      sessionStorage.getItem('last_name')?.trim() &&
      sessionStorage.getItem('last_name').trim() !== 'null'
        ? `${sessionStorage.getItem('first_name').trim()} ${sessionStorage.getItem('last_name').trim()}`
        : sessionStorage.getItem('unique_user_name')

    formData.append('modified_by_name', modified_by_name)
    let config = {
      withCredentials: true,
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    // need to call the service here as Cookie was not getting passed
    const baseURL = process.env.REACT_APP_BASE_API_URL
    let url = `${baseURL}/scenario/uploadUserGuide`
    axios
      .post(url, formData, { ...config })
      .then((resp) => {
        if (resp?.status == 200) {
          setFaqUserGuide(resp?.data?.result)
          setUserGuideLoading(false)
          pushNotification(
            'success',
            'User Guide Uploaded successfully!',
            `Attachment saved successfully.`,
            3
          )
        }
      })
      .catch((err) => {
        setUserGuideLoading(false)
        if (err.response.status === 401 || err.response.status === 403) {
          pushNotification('error', 'Error saving data!', `Error saving data, please try again.`, 3)
        }
      })
  }

  const renderSidebarSkeleton = () => {
    const skeletonCount = faqData.length > 0 ? faqData.length : 3

    return Array.from({ length: skeletonCount }).map((_, index) => (
      <Skeleton.Input key={index} style={{ width: 150, height: 32, marginBottom: 10 }} active />
    ))
  }
  const applicationRole = sessionStorage.getItem('application_role')
  const resultFaqUsfulLinks = fetchedFaqData?.data?.result?.resultFaqUsfulLinks

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <span style={{ color: '#00857C', fontSize: '20px' }}>MERCK Help Section</span>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      width="90vw"
      className="faq-modal"
      centered
      bodyStyle={{ height: '75vh', overflow: 'auto' }}
    >
      <div className="faq-container">
        <div className="faq-sidebar">
          {isFaqDataLoading ? (
            <div>{renderSidebarSkeleton()}</div>
          ) : (
            faqData.map((tab) => (
              <Button
                key={tab.id}
                type="text"
                className={activeTab === tab.id ? 'active' : ''}
                onClick={() => handleTabClick(tab.id)}
              >
                {tab.title}
              </Button>
            ))
          )}
        </div>
        <div className="faq-content">
          {' '}
          {isFaqDataLoading ? <Skeleton active paragraph={{ rows: 10 }} /> : renderContent()}
        </div>
        {!isFaqDataLoading && (
          <div className="faq-links">
            <div className="faq-card">
              <span className="faq-title">User Guide</span>
              {userGuideLoading || isRemoveUserGuideLoading ? (
                <Skeleton active paragraph={{ rows: 1 }} />
              ) : (
                <ul className="file-section">
                  {(!faqUserGuide || faqUserGuide.length === 0) && applicationRole === 'Admin' ? (
                    <li className="file-input">
                      <input
                        type="file"
                        name="user-guide-upload"
                        id="user-guide-upload"
                        onChange={handleFileUpload}
                      />
                    </li>
                  ) : (
                    faqUserGuide.map((fq, index) => (
                      <li key={index}>
                        <p onClick={() => handleDownload(fq)}>{fq?.file_name}</p>
                        {applicationRole === 'Admin' && (
                          <div className="file-delete" onClick={() => handleRemoveUserGuide(fq)}>
                            <span>
                              <DeleteOutlined className="delete-icon" />
                            </span>
                            Remove
                          </div>
                        )}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>

            <div className="faq-card">
              <span className="faq-title">Useful Links</span>
              {resultFaqUsfulLinks?.length > 0 ? (
                <ul>
                  {resultFaqUsfulLinks?.map((val, index) => (
                    <li key={index} className="faq-link">
                      <a href={val?.resource_link} target="_blank" rel="noopener noreferrer">
                        {val?.resource_name}
                        <LinkOutlined className="link-icon" />
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No Links Found</p>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default FaqModal
